import {
  Alert,
  Button,
  EmptyState,
  Pagination,
  SegmentedControl,
  Skeleton,
  Tooltip,
} from '@ntconsult/aluma-react';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TIMELINE_SPANS,
  TIMELINE_STATUSES,
  type TimelineSpan,
  type TimelineStatus,
} from '../hooks/useAllocationFilters';
import { usePaginationLabels } from '../hooks/usePaginationLabels';
import { useTimeline } from '../hooks/useTimeline';
import type { ColumnFilters, EntityFilters } from '../types/Filters';
import type { TimelineFilters } from '../types/Timeline';
import { parseApiDate } from '../utils/data';
import { i18nKeyForError, isSessionError, parseApiError } from '../utils/errors';
import { resolveWindow, windowToApiDates } from '../utils/timelineLayout';
import type { EditAllocationTarget } from './AllocationModal';
import { NoIdentityState } from './NoIdentityState';
import { PerPageSelect } from './PerPageSelect';
import { TimelineGrid } from './TimelineGrid';

/** Paginação por profissional padrão do back — igual à tabela; só vai pra URL quando difere. */
const DEFAULT_PER_PAGE = 30;

const STATUS_LABEL_KEY: Record<
  TimelineStatus,
  'linhaDoTempo.legendaAtiva' | 'linhaDoTempo.legendaFutura' | 'linhaDoTempo.legendaEncerrada'
> = {
  active: 'linhaDoTempo.legendaAtiva',
  upcoming: 'linhaDoTempo.legendaFutura',
  closed: 'linhaDoTempo.legendaEncerrada',
};

/** Chip selecionado = mesmo preenchimento da barra no Gantt (amarra o filtro ao que é desenhado). */
const SELECTED_CHIP_STYLE: Record<TimelineStatus, string> = {
  active: 'border-brand-primary bg-brand-primary text-brand-primary-foreground',
  upcoming: 'border-brand-primary border-dashed bg-brand-primary-subtle text-foreground',
  closed: 'border-border-strong bg-muted text-foreground',
};

type AllocationsTimelineProps = ColumnFilters &
  EntityFilters & {
    professionalIds?: number[];
    notes?: string;
    multiAllocated?: boolean;
    /** Início/Fim compartilhados com a tabela — vazios = janela do range escolhido a partir de hoje. */
    startDate: Date | null;
    endDate: Date | null;
    /** "Hoje" do servidor (`YYYY-MM-DD`); sem ele (ainda carregando), o do navegador. */
    today: string | undefined;
    /** Presente só quando há filtro aplicado: vira o "Limpar filtros" do estado vazio. */
    onClearFilters?: () => void;
    /** Página própria, independente da tabela (`timelinePage` na URL). */
    page: number;
    onPageChange: (page: number) => void;
    /** Profissionais por página (compartilhado com a tabela, `perPage` na URL). */
    perPage: number;
    onPerPageChange: (perPage: number) => void;
    /** Range de meses visível (3/6/12), vindo da URL. Ignorado quando Início+Fim fixam a janela. */
    timelineSpan: TimelineSpan;
    onSpanChange: (span: TimelineSpan) => void;
    /** Status selecionados (vários). Os três = sem filtro (todas). */
    timelineStatus: TimelineStatus[];
    onStatusChange: (statuses: TimelineStatus[]) => void;
    /** Clique numa barra abre o detalhe só-leitura (mesmo modal da tabela). */
    onView: (target: EditAllocationTarget) => void;
  };

function TimelineRangeControl({
  value,
  onChange,
  locked,
}: {
  value: TimelineSpan;
  onChange: (span: TimelineSpan) => void;
  locked: boolean;
}) {
  const { t } = useTranslation('alocacao');
  const control = (
    <SegmentedControl
      size="sm"
      value={value}
      onValueChange={(next) => onChange(next as TimelineSpan)}
      disabled={locked}
      aria-label={t('linhaDoTempo.periodo')}
    >
      {TIMELINE_SPANS.map((span) => (
        <SegmentedControl.Item key={span} value={span}>
          {t('linhaDoTempo.periodoMeses', { count: Number(span) })}
        </SegmentedControl.Item>
      ))}
    </SegmentedControl>
  );
  return locked ? (
    <Tooltip content={t('linhaDoTempo.periodoPorFiltro')}>{control}</Tooltip>
  ) : (
    control
  );
}

/**
 * Chips de alternância (um elemento por status, não checkbox + quadrado): ligado =
 * preenchido com a cor da barra correspondente + check; desligado = contorno neutro.
 * Marcar os três = sem filtro; desmarcar o último volta a marcar todos (nunca "nenhum").
 * Toggle colorido por categoria não existe no DS (ToggleGroup só pinta `bg-muted`), então é ad-hoc.
 */
function TimelineStatusControl({
  value,
  onChange,
}: {
  value: TimelineStatus[];
  onChange: (statuses: TimelineStatus[]) => void;
}) {
  const { t } = useTranslation('alocacao');
  const toggle = (status: TimelineStatus, selected: boolean) =>
    onChange(selected ? [...value, status] : value.filter((current) => current !== status));

  return (
    // biome-ignore lint/a11y/useSemanticElements: grupo de chips de filtro, rotulado pelo texto "Status"
    <div
      role="group"
      aria-label={t('linhaDoTempo.statusFiltro')}
      className="flex flex-wrap items-center gap-x-2 gap-y-2"
    >
      <span className="me-1 text-caption text-foreground-muted">
        {t('linhaDoTempo.statusLabel')}
      </span>
      {TIMELINE_STATUSES.map((status) => {
        const selected = value.includes(status);
        return (
          <button
            key={status}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(status, !selected)}
            className={`inline-flex cursor-pointer items-center gap-1 rounded-md border px-2.5 py-1 text-caption font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 ${
              selected
                ? SELECTED_CHIP_STYLE[status]
                : 'border-border-control bg-surface text-foreground-muted hover:bg-surface-hover'
            }`}
          >
            {/* Slot fixo pro check — reservado sempre pra não deslocar o texto ao alternar. */}
            <span className="inline-flex w-3 justify-center" aria-hidden="true">
              {selected && <Check size={12} />}
            </span>
            {t(STATUS_LABEL_KEY[status])}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Segunda visão de `GET /allocations` (Gantt) — reunião de alinhamento de
 * 2026-09-17. Auto-contido: busca seus próprios dados (`useTimeline`), só é
 * montado quando o usuário troca pra essa aba (`AllocationsPage`), então não
 * concorre com a consulta da tabela. O range de meses (3/6/12) define a janela
 * quando o usuário não fixou Início+Fim nos filtros.
 */
export function AllocationsTimeline({
  startDate,
  endDate,
  today,
  onClearFilters,
  onPageChange,
  perPage,
  onPerPageChange,
  timelineSpan,
  onSpanChange,
  timelineStatus,
  onStatusChange,
  onView,
  ...otherFilters
}: AllocationsTimelineProps) {
  const { t } = useTranslation('alocacao');
  const paginationLabels = usePaginationLabels();

  const months = Number(timelineSpan);
  const rangeLocked = startDate !== null && endDate !== null;
  const visibleWindow = resolveWindow(
    startDate,
    endDate,
    parseApiDate(today) ?? new Date(),
    months,
  );
  // Os três status = sem filtro (o back devolve todas); um subconjunto vai pra API.
  const status = timelineStatus.length === TIMELINE_STATUSES.length ? undefined : timelineStatus;
  const filters: TimelineFilters = {
    ...otherFilters,
    ...windowToApiDates(visibleWindow),
    status,
    perPage: perPage !== DEFAULT_PER_PAGE ? perPage : undefined,
  };

  const timelineQuery = useTimeline(filters);
  const error = parseApiError(timelineQuery.error);

  // Erro de sessão substitui a tela inteira (sem controles nem grade).
  if (timelineQuery.isError && error && isSessionError(error)) {
    return <NoIdentityState code={error.code} />;
  }

  let body: ReactNode;
  // `keepPreviousData`: trocar página/filtro deixaria os dados antigos na tela sem indicação.
  if (timelineQuery.isLoading || timelineQuery.isPlaceholderData) {
    body = (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  } else if (timelineQuery.isError) {
    body = (
      <Alert
        variant="danger"
        title={t((error ? i18nKeyForError(error.code) : 'erros.unknown') as never)}
      />
    );
  } else if (!timelineQuery.data || timelineQuery.data.professionals.length === 0) {
    body = (
      <EmptyState
        title={t('estados.filtroSemResultadoTitulo')}
        description={t('linhaDoTempo.semResultadoDescricao')}
        action={
          onClearFilters && (
            <Button variant="secondary" onClick={onClearFilters}>
              {t('filtros.limpar')}
            </Button>
          )
        }
      />
    );
  } else {
    const { pagination } = timelineQuery.data;
    const totalPages = Math.max(1, Math.ceil(pagination.totalProfessionals / pagination.perPage));
    body = (
      <>
        <TimelineGrid
          professionals={timelineQuery.data.professionals}
          window={visibleWindow}
          today={timelineQuery.data.today}
          onView={onView}
        />
        <div className="flex flex-wrap items-center gap-4">
          <PerPageSelect
            label={t('tabela.profissionaisPorPagina')}
            value={perPage}
            onChange={onPerPageChange}
          />
          {totalPages > 1 && (
            <Pagination
              {...paginationLabels}
              page={filters.page}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-caption text-foreground-muted">{t('linhaDoTempo.periodo')}</span>
          <TimelineRangeControl value={timelineSpan} onChange={onSpanChange} locked={rangeLocked} />
        </div>
        <TimelineStatusControl value={timelineStatus} onChange={onStatusChange} />
      </div>
      {body}
    </div>
  );
}
